/**
 * Write surface: every intent a screen can emit, as a stable hook.
 *
 * These are thin wrappers over `useGameStore` actions. They are stable across
 * renders (zustand actions are defined once), so they can go straight into
 * `useCallback` deps or be passed to memoised children without churn.
 *
 * Every one of them ends up as a validated `ClientMessage` on the socket. None
 * of them change game state locally — the server answers, and the answer is
 * what renders (SPEC §4.2).
 */
import { useGameStore, type GameActions } from './gameStore.js';

/**
 * All actions at once. Zustand actions are created once and never replaced, so
 * reading them off `getState()` is stable and does not subscribe the component
 * to state changes. Prefer the narrow hooks below in leaf components anyway.
 */
export function useGameActions(): GameActions {
  return useGameStore.getState();
}

/* --- turn player ---------------------------------------------------------- */

/** Tap a number on the numbered grid (SPEC §2.3 phase 1). */
export function useSelectQuestion(): GameActions['selectQuestion'] {
  return useGameStore((s) => s.selectQuestion);
}

/** Submit the canonical ranking. This is what fires the 10s guillotine. */
export function useSubmitRanking(): GameActions['submitRanking'] {
  return useGameStore((s) => s.submitRanking);
}

/** The turn player's "Done" button on the justification screen. */
export function useJustificationDone(): GameActions['justificationDone'] {
  return useGameStore((s) => s.justificationDone);
}

/* --- predictors ----------------------------------------------------------- */

/**
 * Both picks, every time, on every change. Partial picks earn nothing
 * (SPEC §2.3 phase 3) — and an *unsubmitted* complete pair still scores, which
 * is why this keeps firing independently of the lock below.
 */
export function useSubmitPrediction(): GameActions['submitPrediction'] {
  return useGameStore((s) => s.submitPrediction);
}

/**
 * "I am done — do not wait for me." Final and one-way: it carries no picks, it
 * locks the pair already on record, and there is no message that undoes it
 * (SPEC §2.3 phase 3).
 */
export function useLockPrediction(): GameActions['lockPrediction'] {
  return useGameStore((s) => s.lockPrediction);
}

/* --- everyone ------------------------------------------------------------- */

/** Anonymous, one per player per question (SPEC §2.5). */
export function useThumbsVote(): GameActions['thumbsVote'] {
  return useGameStore((s) => s.thumbsVote);
}

export function useSetMuted(): GameActions['setMuted'] {
  return useGameStore((s) => s.setMuted);
}

export function useToggleMuted(): GameActions['toggleMuted'] {
  return useGameStore((s) => s.toggleMuted);
}

export function useDismissError(): GameActions['dismissError'] {
  return useGameStore((s) => s.dismissError);
}

/* --- host (SPEC §5.5) ----------------------------------------------------- */

export interface HostControls {
  updateSettings: GameActions['updateSettings'];
  startGame: GameActions['startGame'];
  /** Force-advance out of justification when the turn player stalls. */
  forceAdvance: GameActions['forceAdvance'];
  /** Void a turn whose player has been gone > 30s grace (SPEC §5.4). */
  skipTurn: GameActions['skipTurn'];
  /** For the person who actually went home (SPEC §5.4). */
  removePlayer: GameActions['removePlayer'];
  /** Final leaderboard shows immediately (SPEC §2.2). */
  endGame: GameActions['endGame'];
  /** Same room, same photos, scores reset, numbers still burned (SPEC §2.6). */
  playAgain: GameActions['playAgain'];
}

/**
 * The privileged buttons. Available to any screen; the store still sends them
 * and the server still authorises them, so a non-host calling one gets a
 * `not_host` error rather than a silent success. Gate the UI with `useIsHost()`.
 */
export function useHostControls(): HostControls {
  const updateSettings = useGameStore((s) => s.updateSettings);
  const startGame = useGameStore((s) => s.startGame);
  const forceAdvance = useGameStore((s) => s.forceAdvance);
  const skipTurn = useGameStore((s) => s.skipTurn);
  const removePlayer = useGameStore((s) => s.removePlayer);
  const endGame = useGameStore((s) => s.endGame);
  const playAgain = useGameStore((s) => s.playAgain);

  return { updateSettings, startGame, forceAdvance, skipTurn, removePlayer, endGame, playAgain };
}

/* --- onboarding ----------------------------------------------------------- */

/** Answer the seat picker (SPEC §5.4). Omit the id to ask for a new seat. */
export function useClaimSeat(): GameActions['claimSeat'] {
  return useGameStore((s) => s.claimSeat);
}

export function useSubmitProfile(): GameActions['submitProfile'] {
  return useGameStore((s) => s.submitProfile);
}

export interface DraftActions {
  grantCameraConsent: GameActions['grantCameraConsent'];
  setDraftName: GameActions['setDraftName'];
  setDraftPhoto: GameActions['setDraftPhoto'];
  clearDraftPhoto: GameActions['clearDraftPhoto'];
  resetDraft: GameActions['resetDraft'];
}

export function useDraftActions(): DraftActions {
  const grantCameraConsent = useGameStore((s) => s.grantCameraConsent);
  const setDraftName = useGameStore((s) => s.setDraftName);
  const setDraftPhoto = useGameStore((s) => s.setDraftPhoto);
  const clearDraftPhoto = useGameStore((s) => s.clearDraftPhoto);
  const resetDraft = useGameStore((s) => s.resetDraft);
  return { grantCameraConsent, setDraftName, setDraftPhoto, clearDraftPhoto, resetDraft };
}
